import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuth0 } from "../../components/auth/DevAuth0Provider";
import { fetchActivities, fetchActivityTypes, fetchActivityCustomers } from "../../api/activityApi";

const AdminActivity = () => {
    const { getAccessTokenSilently } = useAuth0();
    
    const [activities, setActivities] = useState([]);
    const [filter, setFilter] = useState({ included: [], excluded: [] });
    const [customerFilter, setCustomerFilter] = useState({ included: [], excluded: [] });
    const [roleFilter, setRoleFilter] = useState({ included: [], excluded: [] });
    const [searchText, setSearchText] = useState('');
    const [timeRange, setTimeRange] = useState('24h');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activityTypes, setActivityTypes] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState({ offset: 0, hasMore: true });

    // Pagination states for table view
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isCalculationComplete, setIsCalculationComplete] = useState(false);
    const tableBodyRef = useRef(null);
    const rowRef = useRef(null);
    const cardRef = useRef(null);

    // Customer filter state
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customersError, setCustomersError] = useState(null);
    const [customersLastFetch, setCustomersLastFetch] = useState(null);

    // Dropdown visibility states
    const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
    const [showCustomersDropdown, setShowCustomersDropdown] = useState(false);
    const [showRolesDropdown, setShowRolesDropdown] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false); // For mobile filters dropdown

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        content: false,
        // Add more sections as needed: cards, form, grid, etc.
    });

    // Load activities from API
    const loadActivities = async (resetPagination = false) => {
        setIsLoading(true);
        setError(null);
        
        try {
            const filters = {
                limit: 50,
                offset: resetPagination ? 0 : pagination.offset
            };
            
            // Add category filter if has selections
            if (filter.included.length > 0) {
                filters.category = filter.included.join(',');
            }
            
            // Add customer filter if has selections
            if (customerFilter.included.length > 0) {
                filters.customer = customerFilter.included.join(',');
            }
            
            // Add role filter if has selections
            if (roleFilter.included.length > 0) {
                filters.role = roleFilter.included.join(',');
            }
            
            // Add time range filter (always included since default is '24h')
            if (timeRange) {
                filters.timeRange = timeRange;
            }

            const response = await fetchActivities(filters, getAccessTokenSilently);
            
            if (resetPagination) {
                setActivities(response.activities || []);
                setPagination({
                    offset: response.pagination?.nextOffset || 0,
                    hasMore: response.hasMore || false
                });
            } else {
                setActivities(prev => [...prev, ...(response.activities || [])]);
                setPagination({
                    offset: response.pagination?.nextOffset || 0,
                    hasMore: response.hasMore || false
                });
            }
            
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error("Failed to load activities:", error);
            setError("Failed to load activities. Please try again later.");
            setActivities([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    // Load activity categories for filter dropdown
    const loadActivityTypes = async () => {
        // Always use the 4 main categories instead of fetching from API
        setActivityTypes([
            { value: 'User Management', label: 'User Management' },
            { value: 'Customer Management', label: 'Customer Management' },
            { value: 'System Activities', label: 'System Activities' },
            { value: 'Admin Activities', label: 'Admin Activities' }
        ]);
    };

    // Load customers for filter dropdown with caching
    const loadCustomers = async (force = false) => {
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
        
        // Check if we have cached data less than 5 minutes old
        if (!force && customersLastFetch && new Date(customersLastFetch) > fiveMinutesAgo && customers.length > 0) {
            return;
        }

        setCustomersLoading(true);
        setCustomersError(null);
        
        try {
            const response = await fetchActivityCustomers(getAccessTokenSilently);
            const sortedCustomers = (response.customers || []).sort((a, b) => a.label.localeCompare(b.label));
            setCustomers(sortedCustomers);
            setCustomersLastFetch(now.toISOString());
        } catch (error) {
            console.error("Failed to load customers:", error);
            setCustomersError("Failed to load customers. Using fallback.");
            // Fallback to some default customers
            setCustomers([
                { value: 'helios', label: 'Helios Core', id: 'org_helios' },
                { value: 'wards', label: "Ward's Rental", id: 'org_wards' }
            ]);
        } finally {
            setCustomersLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        loadActivities(true);
        loadActivityTypes();
        loadCustomers();
    }, [filter, customerFilter, roleFilter, timeRange]);

    // Polling for near real-time updates (every 2 minutes)
    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const latestActivities = await fetchActivities({
                    limit: 10,
                    offset: 0,
                    category: filter.included.length > 0 ? filter.included.join(',') : undefined,
                    customer: customerFilter.included.length > 0 ? customerFilter.included.join(',') : undefined,
                    role: roleFilter.included.length > 0 ? roleFilter.included.join(',') : undefined,
                    timeRange: timeRange
                }, getAccessTokenSilently);

                if (latestActivities.activities && latestActivities.activities.length > 0) {
                    setActivities(prev => {
                        const newActivities = latestActivities.activities.filter(
                            newActivity => !prev.some(existingActivity => existingActivity.id === newActivity.id)
                        );
                        return [...newActivities, ...prev];
                    });
                    setTotalCount(latestActivities.totalCount || 0);
                }
            } catch (error) {
                console.log("Polling failed:", error);
                // Fail silently for polling
            }
        }, 120000); // 2 minutes

        return () => clearInterval(interval);
    }, [filter, customerFilter, roleFilter, timeRange, getAccessTokenSilently]);

    // Dynamic pagination calculation based on container size
    useLayoutEffect(() => {
        const calculateRows = () => {
            if (!tableBodyRef.current) return;

            const containerHeight = tableBodyRef.current.offsetHeight;
            let itemHeight = 0;

            // Determine which view is active and get the appropriate element height
            const isDesktopView = window.innerWidth >= 768; // xl breakpoint

            if (isDesktopView && rowRef.current) {
                // Desktop table view - use table row height
                itemHeight = rowRef.current.offsetHeight;
            } else if (!isDesktopView && cardRef.current) {
                // Mobile card view - use card height
                itemHeight = cardRef.current.offsetHeight;
            } else if (rowRef.current) {
                // Fallback to rowRef if cardRef not available
                itemHeight = rowRef.current.offsetHeight;
            }

            if (itemHeight > 0 && containerHeight > 0) {
                const calculatedRows = Math.floor(containerHeight / itemHeight);
                const newRowsPerPage = Math.max(1, calculatedRows);

                // Only update if the value actually changed to prevent infinite loops
                if (newRowsPerPage !== rowsPerPage) {
                    setRowsPerPage(newRowsPerPage);
                    setCurrentPage(1); // Reset to first page when rows per page changes
                }
                setIsCalculationComplete(true);
            }
        };

        // Delay initial calculation to ensure DOM is ready
        const timeoutId = setTimeout(calculateRows, 100);

        // ResizeObserver for container size changes
        const resizeObserver = new ResizeObserver(() => {
            setIsCalculationComplete(false);
            setTimeout(calculateRows, 50);
        });

        if (tableBodyRef.current) {
            resizeObserver.observe(tableBodyRef.current);
        }

        // Window resize listener for screen size changes
        const handleWindowResize = () => {
            setIsCalculationComplete(false);
            setTimeout(calculateRows, 100);
        };

        window.addEventListener('resize', handleWindowResize);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [activities, rowsPerPage]); // Include rowsPerPage in dependencies

    // Additional effect to handle responsive breakpoint changes
    useEffect(() => {
        const handleBreakpointChange = () => {
            setIsCalculationComplete(false);
            setRowsPerPage(10); // Reset to default
            setTimeout(() => {
                if (!tableBodyRef.current) return;

                const containerHeight = tableBodyRef.current.offsetHeight;
                let itemHeight = 0;

                // Determine which view is active after breakpoint change
                const isDesktopView = window.innerWidth >= 768;

                if (isDesktopView && rowRef.current) {
                    itemHeight = rowRef.current.offsetHeight;
                } else if (!isDesktopView && cardRef.current) {
                    itemHeight = cardRef.current.offsetHeight;
                } else if (rowRef.current) {
                    itemHeight = rowRef.current.offsetHeight;
                }

                if (itemHeight > 0 && containerHeight > 0) {
                    const calculatedRows = Math.floor(containerHeight / itemHeight);
                    const newRowsPerPage = Math.max(1, calculatedRows);
                    setRowsPerPage(newRowsPerPage);
                    setCurrentPage(1);
                    setIsCalculationComplete(true);
                }
            }, 200);
        };

        // Listen for media query changes at xl breakpoint (1280px)
        const mediaQuery = window.matchMedia('(min-width: 768px)');
        mediaQuery.addEventListener('change', handleBreakpointChange);

        return () => {
            mediaQuery.removeEventListener('change', handleBreakpointChange);
        };
    }, []);

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];
        
        // Stagger the animations with delays - customize timing as needed
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));
        
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 300));
    
        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    const getActivityIcon = (type, activity = null) => {
        switch (type) {
            // User Management
            case 'user_login':
                return 'mdi:login';
            case 'user_logout':
                return 'mdi:logout';
            case 'user_created':
                return 'mdi:account-plus';
            case 'user_updated':
                return 'mdi:account-edit';
            case 'user_deleted':
                return 'mdi:account-remove';
            case 'user_role_assigned':
                return 'mdi:account-key';
            case 'user_role_removed':
                return 'mdi:account-key-outline';
            
            // Customer Management
            case 'customer_created':
                return 'mdi:domain-plus';
            case 'customer_updated':
                return 'mdi:domain-edit';
            case 'customer_deleted':
                return 'mdi:domain-remove';
            case 'organization_member_added':
                return 'mdi:account-group-outline';
            case 'organization_member_removed':
                return 'mdi:account-group';
            
            // System Activities
            case 'call_feedback_submitted':
                return 'mdi:message-text';
            case 'ticket_created':
                // Check activity description for specific ticket types
                if (activity && activity.description) {
                    if (activity.description.startsWith('Review Inventory')) {
                        return 'fluent:box-edit-20-regular';
                    } else if (activity.description.startsWith('New Inventory')) {
                        return 'proicons:box-add';
                    }
                }
                return 'mdi:ticket-outline';
            case 'ticket_updated':
                return 'mdi:ticket';
            case 'alert_created':
                return 'mdi:alert-outline';
            case 'zoho_auth_connected':
                return 'mdi:link';
            case 'zoho_auth_disconnected':
                return 'mdi:link-off';
            
            // Admin Activities
            case 'admin_security_setting_changed':
                return 'mdi:security';
            case 'admin_bootstrap_customer_created':
                return 'mdi:bootstrap';
            
            default:
                return 'mdi:information';
        }
    };

    const getActivityColor = (type, activity = null) => {
        switch (type) {
            // User Management - Blue tones
            case 'user_login':
                return 'text-blue-400';
            case 'user_logout':
                return 'text-orange-300';
            case 'user_created':
                return 'text-blue-500';
            case 'user_updated':
                return 'text-blue-600';
            case 'user_deleted':
                return 'text-error';
            case 'user_role_assigned':
                return 'text-purple-400';
            case 'user_role_removed':
                return 'text-purple-300';
            
            // Customer Management - Green tones
            case 'customer_created':
                return 'text-green-400';
            case 'customer_updated':
                return 'text-green-500';
            case 'customer_deleted':
                return 'text-error';
            case 'organization_member_added':
                return 'text-emerald-400';
            case 'organization_member_removed':
                return 'text-emerald-300';
            
            // System Activities - Yellow/Orange tones
            case 'call_feedback_submitted':
                return 'text-yellow-400';
            case 'ticket_created':
                // Check activity description for specific ticket types
                if (activity && activity.description) {
                    if (activity.description.startsWith('Review Inventory')) {
                        return 'text-orange-500';
                    } else if (activity.description.startsWith('New Inventory')) {
                        return 'text-blue-500';
                    }
                }
                return 'text-green-500';
            case 'ticket_updated':
                return 'text-orange-500';
            case 'alert_created':
                return 'text-error';
            case 'zoho_auth_connected':
                return 'text-orange-400';
            case 'zoho_auth_disconnected':
                return 'text-orange-300';
            
            // Admin Activities - Cyan/Teal tones
            case 'admin_security_setting_changed':
                return 'text-cyan-400';
            case 'admin_bootstrap_customer_created':
                return 'text-teal-400';
            
            default:
                return 'text-gray-400';
        }
    };

    // Activity type to category mapping
    const activityTypeCategories = {
        // User Management
        'user_created': 'User Management',
        'user_updated': 'User Management',
        'user_deleted': 'User Management',
        'user_role_assigned': 'User Management',
        'user_role_removed': 'User Management',
        
        // Customer Management
        'customer_created': 'Customer Management',
        'customer_updated': 'Customer Management',
        'customer_deleted': 'Customer Management',
        'organization_member_added': 'Customer Management',
        'organization_member_removed': 'Customer Management',
        
        // System Activities
        'user_login': 'System Activities',
        'user_logout': 'System Activities',
        'call_feedback_submitted': 'System Activities',
        'ticket_created': 'System Activities',
        'ticket_updated': 'System Activities',
        'alert_created': 'System Activities',
        'zoho_auth_connected': 'System Activities',
        'zoho_auth_disconnected': 'System Activities',
        
        // Admin Activities
        'admin_security_setting_changed': 'Admin Activities',
        'admin_bootstrap_customer_created': 'Admin Activities'
    };

    const filteredActivities = activities.filter(activity => {
        // Filter by category
        const activityCategory = activityTypeCategories[activity.type];
        if (filter.excluded.includes(activityCategory)) {
            return false; // Exclude if in excluded list
        }
        if (filter.included.length > 0 && !filter.included.includes(activityCategory)) {
            return false; // If there are inclusions, only show included items
        }
        
        // Filter by customer
        if (customerFilter.excluded.some(excludedCustomer => {
            const customer = customers.find(c => c.value === excludedCustomer);
            return customer && activity.customerName === customer.label;
        })) {
            return false; // Exclude if in excluded list
        }
        if (customerFilter.included.length > 0) {
            const isIncluded = customerFilter.included.some(includedCustomer => {
                const customer = customers.find(c => c.value === includedCustomer);
                return customer && activity.customerName === customer.label;
            });
            if (!isIncluded) return false; // If there are inclusions, only show included items
        }
        
        // Filter by role
        if (roleFilter.excluded.includes(activity.role)) {
            return false; // Exclude if in excluded list
        }
        if (roleFilter.included.length > 0 && !roleFilter.included.includes(activity.role)) {
            return false; // If there are inclusions, only show included items
        }
        
        // Filter by search text
        if (searchText !== '') {
            const searchLower = searchText.toLowerCase();
            const searchableFields = [
                activity.description,
                activity.user,
                activity.customerName,
                activity.email,
                activity.role,
                activity.type,
                activity.details?.customerId,
                activity.details?.endpoint,
                activity.details?.method
            ].filter(Boolean); // Remove undefined/null values
            
            const matchesSearch = searchableFields.some(field => 
                field.toString().toLowerCase().includes(searchLower)
            );
            
            if (!matchesSearch) return false;
        }
        
        // Note: Time range filtering is handled by the backend API
        // No need to filter by time range on frontend as it's already done server-side
        
        return true;
    });

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return 'Invalid Date';
            
            // Format as MM/DD/YYYY HH:MM AM/PM
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            
            return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return 'Invalid Date';
        }
    };

    const formatUserName = (username) => {
        if (!username) return '';
        
        // Check if it's in firstname.lastname format
        if (username.includes('.')) {
            return username
                .split('.')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(' ');
        }
        
        // If not in expected format, just return as is with proper capitalization
        return username.charAt(0).toUpperCase() + username.slice(1).toLowerCase();
    };

    // Generate dynamic description with styled user name
    const getDynamicDescription = (activity) => {
        if (!activity) return '';
        
        const userName = formatUserName(activity.user);
        const originalDescription = activity.description || '';
        
        // Activity-specific description templates
        switch (activity.type) {
            case 'user_login':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> logged in to admin portal</span>
                    </>
                );
            case 'user_logout':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> logged out of admin portal</span>
                    </>
                );
            case 'user_created':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> created user </span>
                        <span className="font-semibold text-accent">{activity.details?.targetUserName || 'Unknown User'}</span>
                    </>
                );
            case 'user_updated':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> updated settings for </span>
                        <span className="font-semibold text-accent">{activity.details?.targetUserName || 'Unknown User'}</span>
                    </>
                );
            case 'user_deleted':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> deleted user </span>
                        <span className="font-semibold text-accent">{activity.details?.targetUserName || 'Unknown User'}</span>
                    </>
                );
            case 'user_role_assigned':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> assigned </span>
                        <span className="font-bold text-accent">{activity.details?.roleName || 'role'}</span>
                        <span className="text-font-secondary"> to </span>
                        <span className="font-bold text-accent">{activity.details?.targetUserName || 'Unknown User'}</span>
                    </>
                );
            case 'customer_created':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary font-semibold"> created new customer: </span>
                        <span className="font-semibold text-accent">{activity.customerName || 'Unknown Customer'}</span>
                    </>
                );
            case 'customer_deleted':
                return (
                    <>
                        <span className="font-semibold">{userName}</span>
                        <span className="text-font-secondary"> deleted customer: </span>
                        <span className="font-semibold text-accent">{activity.customerName || 'Unknown Customer'}</span>
                    </>
                );
            case 'ticket_created':
                if (originalDescription.startsWith('Review Inventory')) {
                    return (
                        <>
                            <span className="font-semibold">{userName}</span>
                            <span className="text-font-secondary"> requested inventory review</span>
                        </>
                    );
                } else if (originalDescription.startsWith('New Inventory')) {
                    return (
                        <>
                            <span className="font-semibold">{userName}</span>
                            <span className="text-font-secondary"> added new inventory item</span>
                        </>
                    );
                } else {
                    return (
                        <>
                            <span className="font-semibold">{userName}</span>
                            <span className="text-font-secondary"> created support ticket</span>
                        </>
                    );
                }
            default:
                // For activities not yet customized, use original description with styled name
                if (originalDescription && userName) {
                    const descriptionWithoutUser = originalDescription.replace(/^User\s+/i, '');
                    return (
                        <>
                            <span className="font-semibold">{userName}</span>
                            <span className="text-font-secondary"> {descriptionWithoutUser}</span>
                        </>
                    );
                }
                return <span className="text-font-secondary">{originalDescription}</span>;
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            // Optional: Add a toast notification here if you have a toast system
        } catch (err) {
            console.error('Failed to copy text: ', err);
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
    };

    // Helper function to highlight search text
    const highlightText = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => 
            regex.test(part) ? (
                <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                    {part}
                </span>
            ) : part
        );
    };

    // Check if any filter is active (excluding time range which is mutually exclusive)
    const isAnyFilterActive = filter.included.length > 0 || filter.excluded.length > 0 || 
                            customerFilter.included.length > 0 || customerFilter.excluded.length > 0 || 
                            roleFilter.included.length > 0 || roleFilter.excluded.length > 0 || 
                            searchText !== '';

    // Reset all filters (excluding time range which is mutually exclusive)
    const resetFilters = () => {
        setFilter({ included: [], excluded: [] });
        setCustomerFilter({ included: [], excluded: [] });
        setRoleFilter({ included: [], excluded: [] });
        setSearchText('');
        // Time range is not reset as it's mutually exclusive
    };

    // Helper function to handle multi-select filter changes
    const handleMultiSelectFilter = (value, currentFilter, setFilterFunc) => {
        const newFilter = { ...currentFilter };
        
        if (newFilter.included.includes(value)) {
            // First click -> Second click: Move from included to excluded
            newFilter.included = newFilter.included.filter(item => item !== value);
            newFilter.excluded = [...newFilter.excluded, value];
        } else if (newFilter.excluded.includes(value)) {
            // Second click -> Third click: Remove from excluded (reset)
            newFilter.excluded = newFilter.excluded.filter(item => item !== value);
        } else {
            // Third click -> First click: Add to included
            newFilter.included = [...newFilter.included, value];
        }
        
        setFilterFunc(newFilter);
    };

    // Helper function to get filter item state
    const getFilterItemState = (value, currentFilter) => {
        if (currentFilter.included.includes(value)) return 'included';
        if (currentFilter.excluded.includes(value)) return 'excluded';
        return 'none';
    };

    // Helper function to get filter item icon
    const getFilterItemIcon = (state) => {
        switch (state) {
            case 'included': return 'material-symbols:check';
            case 'excluded': return 'material-symbols:close';
            default: return null;
        }
    };

    // Helper function to get filter item color
    const getFilterItemColor = (state) => {
        switch (state) {
            case 'included': return 'text-green-500';
            case 'excluded': return 'text-error';
            default: return 'text-font-secondary';
        }
    };

    // Pagination data slicing
    const totalPages = rowsPerPage > 0 ? Math.ceil(filteredActivities.length / rowsPerPage) : 1;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedActivities = isCalculationComplete
        ? filteredActivities.slice(startIndex, startIndex + rowsPerPage)
        : filteredActivities.slice(0, 1);

    return (
        <div className="responsive-page-layout scrollable">
            <div className={`flex flex-col lg:flex-row justify-between lg:items-center items-start w-full gap-4 lg:gap-0 transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="hugeicons:activity-04" className="header-icon" />
                    Activity Log
                </div>
                <div className="relative w-full sm:w-auto">
                    <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Search activities..."
                        className="p-2 sm:p-3 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3"
                    />
                </div>
            </div>

            {/* Filters Section */}
            <div className={`w-full flex flex-row items-center justify-between gap-2 transition-all duration-700 ease-out-in z-10 ${
                animationStates.content
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-row gap-2 items-center w-full justify-between">
                    <div className="flex flex-row gap-2 items-center w-fit">
                        {/* Desktop Filters (md and above) */}
                        <div className="hidden md:flex flex-row gap-2 items-center">
                            {/* Categories Multi-Select */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
                                    className="flex items-center justify-between gap-2 p-2 button-container rounded-md text-sm min-w-[140px]"
                                >
                                    <span>Categories{filter.included.length + filter.excluded.length > 0 ? ` (${filter.included.length + filter.excluded.length})` : ''}</span>
                                    <Icon icon={showCategoriesDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent" />
                                </button>
                                {showCategoriesDropdown && (
                                    <div className="absolute top-full left-0 mt-2 bg-background shadow-light rounded-md border border-secondary-50 z-50 min-w-[200px] max-h-60 scrollable overflow-y-scroll">
                                        {activityTypes.map(type => {
                                            const state = getFilterItemState(type.value, filter);
                                            const icon = getFilterItemIcon(state);
                                            const color = getFilterItemColor(state);
                                            return (
                                                <button
                                                    key={type.value}
                                                    onClick={() => handleMultiSelectFilter(type.value, filter, setFilter)}
                                                    className={`w-full text-left p-2 hover:bg-background transition-colors flex items-center justify-between ${color}`}
                                                >
                                                    <span>{type.label}</span>
                                                    {icon && <Icon icon={icon} className="text-lg" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Click outside to close dropdown */}
                                {showCategoriesDropdown && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowCategoriesDropdown(false)}
                                    />
                                )}
                            </div>
                            
                            {/* Clients Multi-Select */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowCustomersDropdown(!showCustomersDropdown)}
                                    className="flex items-center justify-between gap-2 p-2 button-container rounded-md text-sm min-w-[120px]"
                                    disabled={customersLoading}
                                >
                                    <span>{customersLoading ? 'Loading...' : `Clients${customerFilter.included.length + customerFilter.excluded.length > 0 ? ` (${customerFilter.included.length + customerFilter.excluded.length})` : ''}`}</span>
                                    <Icon icon={showCustomersDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent" />
                                </button>
                                {showCustomersDropdown && !customersLoading && (
                                    <div className="absolute top-full left-0 mt-2 bg-background shadow-light rounded-md border border-secondary-50 z-50 min-w-[200px] max-h-60 scrollable overflow-y-scroll">
                                        {customers.map(customer => {
                                            const state = getFilterItemState(customer.value, customerFilter);
                                            const icon = getFilterItemIcon(state);
                                            const color = getFilterItemColor(state);
                                            return (
                                                <button
                                                    key={customer.value}
                                                    onClick={() => handleMultiSelectFilter(customer.value, customerFilter, setCustomerFilter)}
                                                    className={`w-full text-left p-2 hover:bg-background transition-colors flex items-center justify-between ${color}`}
                                                >
                                                    <span>{customer.label}</span>
                                                    {icon && <Icon icon={icon} className="text-lg" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Click outside to close dropdown */}
                                {showCustomersDropdown && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowCustomersDropdown(false)}
                                    />
                                )}
                            </div>
                            
                            {/* Roles Multi-Select */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowRolesDropdown(!showRolesDropdown)}
                                    className="flex items-center justify-between gap-2 p-2 button-container rounded-md text-sm min-w-[100px]"
                                >
                                    <span>Roles{roleFilter.included.length + roleFilter.excluded.length > 0 ? ` (${roleFilter.included.length + roleFilter.excluded.length})` : ''}</span>
                                    <Icon icon={showRolesDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent" />
                                </button>
                                {showRolesDropdown && (
                                    <div className="absolute top-full left-0 mt-2 bg-background shadow-light rounded-md border border-secondary-50 z-50 min-w-[180px] max-h-60 scrollable overflow-y-scroll">
                                        {['helios_admin', 'client_admin', 'client_user'].map(role => {
                                            const state = getFilterItemState(role, roleFilter);
                                            const icon = getFilterItemIcon(state);
                                            const color = getFilterItemColor(state);
                                            const roleLabel = role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                                            return (
                                                <button
                                                    key={role}
                                                    onClick={() => handleMultiSelectFilter(role, roleFilter, setRoleFilter)}
                                                    className={`w-full text-left p-2 hover:bg-background transition-colors flex items-center justify-between ${color}`}
                                                >
                                                    <span>{roleLabel}</span>
                                                    {icon && <Icon icon={icon} className="text-lg" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Click outside to close dropdown */}
                                {showRolesDropdown && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowRolesDropdown(false)}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Mobile All Filters Button (below md) */}
                        <div className="md:hidden w-fit relative">
                            <button
                                onClick={() => setShowMobileFilters(!showMobileFilters)}
                                className="flex flex-row items-center gap-2 p-2 button-container text-sm rounded-md transition-colors w-full justify-center"
                            >
                                <Icon icon="material-symbols:filter-list" className="text-xl text-accent" />
                                All Filters
                                {isAnyFilterActive && (
                                    <span className="bg-primary text-white text-xs px-2 py-1 rounded-full ml-2">
                                        {(filter.included.length + filter.excluded.length) + 
                                         (customerFilter.included.length + customerFilter.excluded.length) + 
                                         (roleFilter.included.length + roleFilter.excluded.length)}
                                    </span>
                                )}
                            </button>

                            {/* Mobile Filters Dropdown */}
                            {showMobileFilters && (
                                <div className="absolute top-full w-fit left-0 mt-2 button-container rounded-md shadow-lg border border-bgAccent z-50">
                                    <div className="flex flex-col">
                                        {/* Categories Filter */}
                                        <div className="p-2 border-b border-bgAccent">
                                            <div className="font-medium mb-1">Categories</div>
                                            <div className="max-h-40 scrollable overflow-y-scroll">
                                                {activityTypes.map(type => {
                                                    const state = getFilterItemState(type.value, filter);
                                                    const icon = getFilterItemIcon(state);
                                                    const color = getFilterItemColor(state);
                                                    return (
                                                        <button
                                                            key={type.value}
                                                            onClick={() => handleMultiSelectFilter(type.value, filter, setFilter)}
                                                            className={`w-full whitespace-nowrap text-left p-2 hover:bg-bgSecondary transition-colors flex items-center justify-between rounded-md ${color}`}
                                                        >
                                                            <span>{type.label}</span>
                                                            {icon && <Icon icon={icon} className="text-lg" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Clients Filter */}
                                        <div className="p-2 border-b border-bgAccent">
                                            <div className="font-medium mb-1">Clients</div>
                                            <div className="max-h-40 scrollable overflow-y-scroll">
                                                {customers.map(customer => {
                                                    const state = getFilterItemState(customer.value, customerFilter);
                                                    const icon = getFilterItemIcon(state);
                                                    const color = getFilterItemColor(state);
                                                    return (
                                                        <button
                                                            key={customer.value}
                                                            onClick={() => handleMultiSelectFilter(customer.value, customerFilter, setCustomerFilter)}
                                                            className={`w-full whitespace-nowrap text-left p-2 hover:bg-bgSecondary transition-colors flex items-center justify-between rounded-md ${color}`}
                                                        >
                                                            <span>{customer.label}</span>
                                                            {icon && <Icon icon={icon} className="text-lg" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Roles Filter */}
                                        <div className="p-2 border-b border-bgAccent">
                                            <div className="font-medium mb-1">Roles</div>
                                            <div className="max-h-40 scrollable overflow-y-scroll">
                                                {['helios_admin', 'client_admin', 'client_user'].map(role => {
                                                    const state = getFilterItemState(role, roleFilter);
                                                    const icon = getFilterItemIcon(state);
                                                    const color = getFilterItemColor(state);
                                                    const roleLabel = role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                    return (
                                                        <button
                                                            key={role}
                                                            onClick={() => handleMultiSelectFilter(role, roleFilter, setRoleFilter)}
                                                            className={`w-full whitespace-nowrap text-left p-2 hover:bg-bgSecondary transition-colors flex items-center justify-between rounded-md ${color}`}
                                                        >
                                                            <span>{roleLabel}</span>
                                                            {icon && <Icon icon={icon} className="text-lg" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Reset Filters */}
                                        {isAnyFilterActive && (
                                            <button
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowMobileFilters(false);
                                                }}
                                                className="flex flex-row items-center gap-2 p-2 text-sm rounded-md transition-colors justify-center mt-2 border-t border-border"
                                            >
                                                <Icon icon="material-symbols:refresh" className="text-xl text-error" />
                                                Reset Filters
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Click outside to close mobile filters */}
                            {showMobileFilters && (
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setShowMobileFilters(false)}
                                />
                            )}
                        </div>

                        {/* Reset Filters - Desktop */}
                        {isAnyFilterActive && (
                            <button
                                onClick={resetFilters}
                                className="hidden md:flex flex-row items-center gap-2 p-2 button-container text-sm ml-auto rounded-md hover:shadow-light transition-colors"
                            >
                                <Icon icon="material-symbols:refresh" className="text-xl text-accent" />
                                Reset Filters
                            </button>
                        )}
                        
                        {totalCount > 0 && (
                            <div className="hidden md:flex items-center gap-1 text-sm text-font-secondary whitespace-nowrap">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span><b className="text-primary">{totalCount}</b> total</span>
                            </div>
                        )}
                    </div>
                    
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="p-2 button-container rounded-md text-sm"
                    >
                        <option value="24h">Last 24 Hours</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                    </select>
                </div>
            </div>

            <div className={`w-full h-full flex flex-col transition-all duration-1000 ease-out-in ${
                animationStates.content
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-4 scale-95'
            }`}>
                {error && (
                    <div className="text-center mb-4 p-4">
                        <p className="text-yellow-500">⚠️ {error}</p>
                    </div>
                )}
                {customersError && (
                    <div className="text-center mb-4 p-4">
                        <p className="text-orange-500">⚠️ {customersError}</p>
                    </div>
                )}

                {isLoading && activities.length === 0 ? (
                    <div className="text-center py-8">
                        <Icon icon="eos-icons:loading" className="animate-spin text-4xl text-accent mb-4" />
                        <p className="text-accent">Loading activities...</p>
                    </div>
                ) : filteredActivities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full rounded-lg p-8">
                        <Icon icon="hugeicons:activity-04" className="text-6xl text-gray-400 mb-4" />
                        <h3 className="text-xl text-font-primary mb-2">No recent activity</h3>
                        <p className="text-font-secondary text-center">
                            {isAnyFilterActive 
                                ? "No activities found for the selected filters. Try adjusting your search criteria." 
                                : "No recent activity to display."}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table Header (xl and above) */}
                        <div className="flex-shrink-0 hidden md:block">
                            <table className="w-full table-auto text-left border-collapse text-sm">
                                <thead>
                                    <tr className="text-font-primary rounded-md h-[48px] card-container font-semibold">
                                        <th className="w-[40px] truncate max-w-[40px] pl-4 pr-2">Type</th>
                                        <th className="w-[280px] truncate max-w-[280px] pr-2">Activity</th>
                                        <th className="w-[100px] truncate max-w-[100px] pr-2">Customer</th>
                                        <th className="w-[150px] truncate max-w-[150px] pr-2">Email</th>
                                        <th className="w-[80px] truncate max-w-[80px] pr-2">Role</th>
                                        <th className="w-[100px] truncate max-w-[100px] pr-4">Timestamp</th>
                                    </tr>
                                </thead>
                            </table>
                        </div>

                        {/* Mobile Header (below xl) */}
                        <div className="flex-shrink-0 md:hidden">
                            <div className="flex items-center justify-between px-3 py-2 text-font-primary text-sm h-[48px] card-container font-semibold">
                                <span className="font-normal">Activities</span>
                                <span className="font-normal text-xs">
                                    {paginatedActivities.length} displayed
                                </span>
                            </div>
                        </div>

                        <div ref={tableBodyRef} className="flex-1 overflow-y-auto scrollable">
                            {/* Desktop Table View (xl and above) */}
                            <table className="w-full table-auto text-left border-collapse text-sm hidden md:table">
                                <tbody>
                                    {paginatedActivities.map((activity, index) => {
                                        const ref = index === 0 ? rowRef : null;
                                        return (
                                            <tr
                                                key={activity.id}
                                                ref={ref}
                                                className="h-[56px] border-b border-border text-font-secondary interactive-card-container cursor-default transition-all duration-400 ease-out-in"
                                            >
                                                <td className="w-[40px] truncate max-w-[40px] pl-4 pr-2">
                                                    <Icon
                                                        icon={getActivityIcon(activity.type, activity)}
                                                        className={`text-2xl ${getActivityColor(activity.type, activity)}`}
                                                    />
                                                </td>
                                                <td className="w-[280px] truncate max-w-[280px] pr-2 text-font-primary">
                                                    {getDynamicDescription(activity)}
                                                </td>
                                                <td className="w-[100px] truncate max-w-[100px] pr-2">
                                                    {highlightText(activity.customerName || 'N/A', searchText)}
                                                </td>
                                                <td className="w-[150px] truncate max-w-[150px] pr-2">
                                                    <div className="flex items-center gap-1">
                                                        <span className="truncate">{highlightText(activity.email || 'N/A', searchText)}</span>
                                                        {activity.email && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    copyToClipboard(activity.email);
                                                                }}
                                                                className="flex-shrink-0 p-1 hover:bg-background rounded transition-colors duration-200"
                                                                title="Copy email"
                                                            >
                                                                <Icon icon="mdi:content-copy" className="text-sm text-accent" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="w-[80px] truncate max-w-[80px] pr-2">
                                                    {highlightText(activity.role || 'N/A', searchText)}
                                                </td>
                                                <td className="w-[100px] truncate max-w-[100px] pr-4">
                                                    {formatTimestamp(activity.timestamp)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Mobile Card View (below xl) */}
                            <div className="md:hidden space-y-2 py-2">
                                {paginatedActivities.map((activity, index) => {
                                    const ref = index === 0 ? cardRef : null;
                                    return (
                                        <div
                                            key={activity.id}
                                            ref={ref}
                                            className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] bg-background shadow-light border border-border"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <Icon
                                                        icon={getActivityIcon(activity.type, activity)}
                                                        className={`text-2xl ${getActivityColor(activity.type, activity)} flex-shrink-0`}
                                                    />
                                                                                                                                                             <div className="flex flex-col">
                                                        <span className="font-semibold text-primary text-sm">
                                                            {getDynamicDescription(activity)}
                                                        </span>
                                                    </div>
                                                 </div>
                                                 <span className="text-xs text-font-secondary">
                                                     {formatTimestamp(activity.timestamp)}
                                                 </span>
                                             </div>

                                             <div className="flex items-center justify-between mb-2">
                                                 <span className="text-xs text-font-secondary">
                                                     {highlightText(activity.customerName || 'N/A', searchText)}
                                                 </span>
                                                 {activity.role && (
                                                     <span className="text-xs text-font-secondary">
                                                         {highlightText(activity.role, searchText)}
                                                     </span>
                                                 )}
                                             </div>

                                             {activity.email && (
                                                 <div className="flex items-center gap-1">
                                                     <span className="text-xs text-font-secondary truncate">
                                                         {highlightText(activity.email, searchText)}
                                                     </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(activity.email);
                                                        }}
                                                        className="flex-shrink-0 p-1 hover:bg-background rounded transition-colors duration-200"
                                                        title="Copy email"
                                                    >
                                                        <Icon icon="mdi:content-copy" className="text-sm text-accent" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {paginatedActivities.length === 0 && (
                                <div className="text-center py-10 text-gray-400">No activities found.</div>
                            )}
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex-shrink-0 flex items-center justify-between md:px-4 py-3 border-t border-border text-sm">
                            <div>
                                <span className="text-font-secondary">
                                    Showing <span className="font-semibold text-primary">{Math.min(startIndex + 1, filteredActivities.length)}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, filteredActivities.length)}</span> of <span className="font-semibold text-primary">{filteredActivities.length}</span> results
                                </span>
                            </div>
                            <div className="flex items-center gap-2 md:px-4">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 disabled:opacity-50 disabled:cursor-not-allowed bg-background shadow-light hover:button-container"
                                >
                                    <Icon icon="icon-park-outline:double-left" className="text-xl text-accent" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 disabled:opacity-50 disabled:cursor-not-allowed bg-background shadow-light hover:button-container"
                                >
                                    <Icon icon="material-symbols:arrow-back-ios-new" className="text-lg text-accent" />
                                </button>
                                <span className="text-font-secondary flex flex-row items-center gap-1">
                                    <span className="font-bold text-accent">{currentPage}</span> of <span className="font-bold">{totalPages}</span>
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 disabled:opacity-50 disabled:cursor-not-allowed bg-background shadow-light hover:button-container"
                                >
                                    <Icon icon="material-symbols:arrow-forward-ios" className="text-lg text-accent" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 disabled:opacity-50 disabled:cursor-not-allowed bg-background shadow-light hover:button-container"
                                >
                                    <Icon icon="icon-park-outline:double-right" className="text-xl text-accent" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminActivity;